"use client";

import { useState, useEffect, useRef } from "react";
import { AiProgressOverlay } from "@/components/ui/ai-progress-overlay";
import type { AiStep } from "@/components/ui/ai-progress-overlay";
import { readBrowserAttribution } from "@/components/analytics/browser-attribution";
import { FreeJobPackStartCta } from "@/components/marketing/free-job-pack-start-cta";

const DOC_TYPES = [
  { id: "rams", label: "RAMS", desc: "Risk Assessment & Method Statement" },
  { id: "cpp", label: "CPP", desc: "Construction Phase Plan (Domestic/Only Contractor)" },
  { id: "toolbox", label: "Toolbox Talk", desc: "Toolbox Talk Briefing" },
  { id: "induction", label: "Site Induction", desc: "Site Induction Pack" },
  { id: "emergency", label: "Emergency Plan", desc: "Emergency Response Plan" },
] as const;

const STEPS_BY_TYPE: Record<string, AiStep[]> = {
  rams: [
    { label: "Reading your project brief" },
    { label: "Identifying site hazards", detail: "Assessing risks specific to your job..." },
    { label: "Building risk assessment", detail: "L×S ratings and control measures..." },
    { label: "Writing method statement", detail: "Step-by-step safe system of work..." },
    { label: "Adding COSHH section", detail: "Substances, hazards, and controls..." },
    { label: "Preparing emergency procedures" },
    { label: "Generating PDF", detail: "Formatting your document..." },
  ],
  cpp: [
    { label: "Reading your project brief" },
    { label: "Identifying site management arrangements" },
    { label: "Building welfare provisions", detail: "Facilities and site rules..." },
    { label: "Writing emergency procedures" },
    { label: "Adding risk management", detail: "CDM 2015 compliance..." },
    { label: "Generating PDF", detail: "Formatting your document..." },
  ],
  toolbox: [
    { label: "Reading your topic" },
    { label: "Researching key points", detail: "Regulations and best practice..." },
    { label: "Writing talk content" },
    { label: "Adding discussion points" },
    { label: "Generating PDF", detail: "Formatting your document..." },
  ],
  induction: [
    { label: "Reading your project brief" },
    { label: "Building site rules" },
    { label: "Adding emergency info", detail: "Assembly points, first aiders..." },
    { label: "Writing PPE requirements" },
    { label: "Generating PDF", detail: "Formatting your document..." },
  ],
  emergency: [
    { label: "Reading your project brief" },
    { label: "Identifying emergency scenarios" },
    { label: "Writing response procedures", detail: "Fire, injury, spillage..." },
    { label: "Adding contact details" },
    { label: "Generating PDF", detail: "Formatting your document..." },
  ],
};

const examples: Record<string, string[]> = {
  rams: [
    "Kitchen extension — knocking through load-bearing wall, steel beam install",
    "Loft conversion with dormer — timber frame, roofing, Velux windows",
    "External scaffolding for re-render on a 3-storey Victorian terrace",
    "Bathroom refurb — strip out, first-fix plumbing, tiling",
  ],
  cpp: [
    "Two-storey rear extension on a semi-detached house in a residential area",
    "Full house refurb including rewire, re-plumb, new kitchen and bathroom",
    "New build detached garage with utility room — domestic client",
  ],
  toolbox: [
    "Working at height — ladder and scaffold safety",
    "Manual handling on site — blocks, lintels, and bags of cement",
    "Silica dust exposure when cutting concrete and block",
  ],
  induction: [
    "Residential extension project in a suburban cul-de-sac",
    "Commercial office fit-out in a busy town centre",
    "School refurbishment during term time — live site with public access",
  ],
  emergency: [
    "Residential new build on a greenfield site",
    "Refurb of an occupied care home — elderly residents present",
    "Demolition and rebuild on a tight city centre plot",
  ],
};

export function DemoRamsGenerator({
  initialDescription,
}: {
  initialDescription?: string;
} = {}) {
  const [docType, setDocType] = useState("rams");
  const [description, setDescription] = useState(initialDescription ?? "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    pdfUrl: string;
    title: string;
    hazardCount: number;
    docType: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const stepTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Lead capture state
  const [leadEmail, setLeadEmail] = useState("");
  const [leadName, setLeadName] = useState("");
  const [leadCompany, setLeadCompany] = useState("");
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadError, setLeadError] = useState<string | null>(null);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [leadConsent, setLeadConsent] = useState(false);

  // Sync initialDescription from photo uploader
  useEffect(() => {
    if (initialDescription) {
      setDescription(initialDescription);
      setDocType("rams");
    }
  }, [initialDescription]);

  const activeSteps = STEPS_BY_TYPE[docType] ?? STEPS_BY_TYPE.rams!;
  const activeExamples = examples[docType] ?? examples.rams!;
  const selectedDoc = DOC_TYPES.find((d) => d.id === docType)!;

  useEffect(() => {
    if (loading) {
      setCurrentStep(0);
      let step = 0;
      stepTimerRef.current = setInterval(() => {
        step++;
        if (step < activeSteps.length - 1) {
          setCurrentStep(step);
        } else {
          setCurrentStep(activeSteps.length - 1);
          if (stepTimerRef.current) clearInterval(stepTimerRef.current);
        }
      }, 4000);
    } else {
      if (stepTimerRef.current) {
        clearInterval(stepTimerRef.current);
        stepTimerRef.current = null;
      }
    }
    return () => {
      if (stepTimerRef.current) clearInterval(stepTimerRef.current);
    };
  }, [loading, activeSteps.length]);

  const generate = async () => {
    if (!description.trim() || description.trim().length < 10) {
      setError("Tell us a bit more — what's the job?");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch("/api/demo/generate-rams", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          description: description.trim(),
          docType,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 429) {
          setError(
            "You've used your 3 free demo generations for today. Sign up free for unlimited!"
          );
        } else {
          setError(data.error || "Something went wrong — give it another go");
        }
        return;
      }

      setCurrentStep(activeSteps.length);
      setResult({
        pdfUrl: data.pdfUrl,
        title: data.title,
        hazardCount: data.hazardCount,
        docType,
      });
    } catch {
      setError("Network error — check your connection and try again");
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async () => {
    if (!leadEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(leadEmail.trim())) {
      setLeadError("Please enter a valid email address");
      return;
    }
    setLeadSubmitting(true);
    setLeadError(null);
    try {
      const res = await fetch("/api/demo/capture-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: leadEmail.trim(),
          name: leadName.trim() || undefined,
          company: leadCompany.trim() || undefined,
          marketingConsent: leadConsent,
          docType: result?.docType,
          docTitle: result?.title,
          pdfUrl: result?.pdfUrl,
          attribution: readBrowserAttribution(),
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        setLeadError(data.error || "Something went wrong");
        return;
      }
      setLeadCaptured(true);
    } catch {
      setLeadError("Network error — please try again");
    } finally {
      setLeadSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setDescription("");
    setError(null);
    setLeadEmail("");
    setLeadName("");
    setLeadCompany("");
    setLeadCaptured(false);
    setLeadConsent(false);
    setLeadError(null);
  };

  return (
    <div className="mt-10">
      {/* Full-screen waterfall overlay */}
      {loading && (
        // pointer-events-none on the backdrop so the marketing header / Pricing /
        // Sign up links stay clickable during the 30-60s generation wait — they
        // visibly show through the scrim and were a measured source of dead
        // sign_up_start taps on /demo. Inner card re-enables pointer events.
        <div className="pointer-events-none fixed inset-0 z-50 bg-white/95 backdrop-blur-sm flex items-center justify-center">
          <div className="pointer-events-auto mx-auto max-w-md px-4 flex flex-col items-center text-center">
            <div className="mb-8">
              <div className="mx-auto h-16 w-16 rounded-full bg-accent-light flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-brand-700 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Creating your {selectedDoc.label}
              </h2>
              <p className="text-sm text-gray-500 mt-2">
                This usually takes 30–60 seconds. Please don&apos;t close this page.
              </p>
            </div>
            <div className="w-full">
              <AiProgressOverlay steps={activeSteps} currentStep={currentStep} mode="inline" />
            </div>
          </div>
        </div>
      )}

      {/* Result — shown instead of input after generation */}
      {result ? (
        <div className="space-y-4">
          <div className="rounded-2xl border-2 border-done-line bg-done-bg p-6">
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-done-bg p-3">
                <svg
                  className="h-6 w-6 text-done"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-semibold text-done">
                  Your {DOC_TYPES.find((d) => d.id === result.docType)?.label || "document"} is ready!
                </p>
                <p className="mt-1 text-sm text-done">
                  <strong>{result.title}</strong>
                  {result.hazardCount > 0 && (
                    <> — {result.hazardCount} hazards assessed with full control measures, methodology, COSHH, and emergency procedures.</>
                  )}
                </p>
                {result.docType === "cpp" && (
                  <p className="mt-2 text-xs text-done bg-done-bg rounded-md px-3 py-2">
                    📋 This is a <strong>lightweight CPP</strong> for domestic projects where you&apos;re the only contractor on site. The full version in The Site Book includes principal contractor duties, multi-contractor coordination, and detailed CDM 2015 compliance sections.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Email gate or download */}
          {leadCaptured ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg shadow-amber-600/5">
              <div className="text-center">
                <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-done-bg flex items-center justify-center">
                  <svg className="h-6 w-6 text-done" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                </div>
                <p className="font-semibold text-gray-900">Your PDF is ready to download</p>
                {/* Review M12: the welcome email only sends when marketing
                    consent was ticked — don't claim otherwise. */}
                {leadConsent ? (
                  <p className="mt-1 text-sm text-gray-500">We&apos;ve also sent a copy to {leadEmail}</p>
                ) : null}

                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-center">
                  <a
                    href={result.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center rounded-lg bg-done px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-done"
                  >
                    ↓ Download PDF
                  </a>
                  <FreeJobPackStartCta
                    draft={{
                      trackingKey: "demo_generated_result",
                      title: result.title,
                      ctaLabel: "Create my free job pack",
                      scopeDescription: `Demo job description:\n${description.trim()}`,
                      domesticClient: true,
                      estimatedDuration: "1-4 weeks",
                    }}
                    utmContent="demo_generated_result"
                    ctaLocation="demo-result"
                    className="inline-flex items-center justify-center rounded-lg bg-accent px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-amber-600/25 transition-colors hover:bg-accent-dark"
                  >
                    Create my free job pack →
                  </FreeJobPackStartCta>
                </div>

                <p className="mt-4 text-xs text-gray-500">
                  💡 This is a sample to show the format. Sign up free to get:
                  your company name &amp; logo, digital signatures, share links,
                  and unlimited documents tailored to your real projects.
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg shadow-amber-600/5">
              <p className="font-semibold text-gray-900">
                Enter your email to download the PDF
              </p>
              <p className="mt-1 text-sm text-gray-500">
                We&apos;ll send a copy to your inbox too. No spam, no nonsense.
              </p>

              <div className="mt-4 space-y-3">
                <div>
                  <input
                    type="email"
                    value={leadEmail}
                    onChange={(e) => { setLeadEmail(e.target.value); setLeadError(null); }}
                    placeholder="your@email.com"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={leadName}
                    onChange={(e) => setLeadName(e.target.value)}
                    placeholder="Your name (optional)"
                    className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                  />
                  <input
                    type="text"
                    value={leadCompany}
                    onChange={(e) => setLeadCompany(e.target.value)}
                    placeholder="Company name (optional)"
                    className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                  />
                </div>

                {leadError && (
                  <p className="text-sm font-medium text-danger">{leadError}</p>
                )}

                <button
                  type="button"
                  onClick={submitLead}
                  disabled={leadSubmitting || !leadEmail.trim()}
                  className="w-full rounded-lg bg-done px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-done disabled:opacity-50"
                >
                  {leadSubmitting ? "Sending..." : "↓ Get your PDF"}
                </button>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={leadConsent}
                    onChange={(e) => setLeadConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-700 focus:ring-accent"
                  />
                  <span className="text-xs text-gray-500">
                    I&apos;d like to receive occasional tips on CDM compliance and construction safety. Unsubscribe anytime.
                  </span>
                </label>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={reset}
            className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
          >
            ← Generate another document
          </button>
        </div>
      ) : (
        <>
          {/* Document type selector */}
          <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
            {DOC_TYPES.map((doc) => (
              <button
                key={doc.id}
                type="button"
                onClick={() => {
                  setDocType(doc.id);
                  setDescription("");
                  setError(null);
                }}
                className={`shrink-0 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                  docType === doc.id
                    ? "bg-accent text-white shadow-lg shadow-amber-600/25"
                    : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {doc.label}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg shadow-amber-600/5">
            <label
              htmlFor="demo-desc"
              className="block text-sm font-semibold text-gray-900"
            >
              {docType === "toolbox"
                ? "What topic should the toolbox talk cover?"
                : "Describe the project"}
            </label>
            <textarea
              id="demo-desc"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setError(null);
              }}
              placeholder={
                docType === "toolbox"
                  ? "e.g. Working at height — ladder safety, scaffold inspection, edge protection"
                  : "e.g. Rear single-storey extension — strip foundations, blockwork, flat roof with EPDM"
              }
              rows={3}
              maxLength={500}
              disabled={loading}
              className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-50"
            />

            {/* Quick examples */}
            <div className="mt-3 flex flex-wrap gap-2">
              {activeExamples.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => {
                    setDescription(ex);
                    setError(null);
                  }}
                  disabled={loading}
                  className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-600 transition-colors hover:border-accent/30 hover:bg-accent-light hover:text-brand-700 disabled:opacity-50"
                >
                  {ex.length > 55 ? ex.substring(0, 55) + "…" : ex}
                </button>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs text-gray-400">
                {description.length}/500 characters
              </span>
              <button
                type="button"
                onClick={generate}
                disabled={loading || !description.trim()}
                className="rounded-lg bg-accent px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-amber-600/25 transition-colors hover:bg-accent-dark disabled:opacity-50"
              >
                Generate {selectedDoc.label} →
              </button>
            </div>

            {error && (
              <p className="mt-3 text-sm font-medium text-danger">{error}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
